import { IAutoMapper } from "../../../../../../core/automapper/interfaces";
import { DateHelper } from "../../../../../../utilities/contracts/date-helper";
import { StringHelper } from "../../../../../../utilities/contracts/string-helper";
import { DEFAULT_REVIEW_DEADLINE_DAYS, ReviewDeadlineState, daysRemaining, getReviewDeadlineState } from "./review-deadline";

const OVERREADER2_ROLE_NAME = "Overreader2";

export const mapFeedbackChallengeQueryItem = (automapper: IAutoMapper): void => {
  automapper.createMap("default", "FeedbackChallengeQueryItem")
    .forMember("chaseId", o => o.chaseId)
    .forMember("projectId", o => o.projectId)
    .forMember("projectName", o => o.projectName)
    .forMember("measureCode", o => o.measureCode)
    .forMember("codingUserId", o => o.codingUserId)
    .forMember("codingUserName", o => o.codingUserName)
    .forMember("codingDate", o => o.codingDate)
    .forMember("qaUserId", o => o.qaUserId)
    .forMember("qaUserName", o => o.qaUserName)
    .forMember("qaRoleName", o => o.qaRoleName)
    .forMember("qaDate", o => o.qaDate)
    .forMember("overreadLevel", o => o.qaRoleName === OVERREADER2_ROLE_NAME ? "OR II" : "OR I")
    .forMember("managerUserId", o => o.managerUserId)
    .forMember("managerUserName", o => o.managerUserName)
    .forMember("challengeDate", o => o.challengeDate)
    .forMember("reviewDeadlineDays", o => o.reviewDeadlineDays)
    .forMember("errorCount", o => o.errorCount)
    .forMember("errorReviewCompletedCount", o => o.errorReviewCompletedCount)
    .forMember("errorReviewToBeCompletedCount", o => o.errorReviewToBeCompletedCount)
    .forMember("chaseOverreadFeedbackStatus", o => o.chaseOverreadFeedbackStatus)
    .forMember("codingReviewMode", o => o.codingReviewMode)
    .forMember("recordCount", o => o.recordCount);
};

export class FeedbackChallengeQueryItem {
  chaseId: number;
  projectId: number;
  projectName: string;
  measureCode: string;
  codingUserId: number;
  codingUserName: string;
  codingDate: Date;
  qaUserId: number;
  qaUserName: string;
  qaRoleName: string;
  qaDate: Date;
  overreadLevel: string;
  managerUserId: number;
  managerUserName: string;
  challengeDate: Date;
  reviewDeadlineDays: number;
  errorCount: number;
  errorReviewCompletedCount: number;
  errorReviewToBeCompletedCount: number;
  chaseOverreadFeedbackStatus: string;
  codingReviewMode: string;
  recordCount: number;

  constructor(options: Partial<FeedbackChallengeQueryItem>) {
    this.chaseId = options.chaseId;
    this.projectId = options.projectId;
    this.projectName = StringHelper.clean(options.projectName);
    this.measureCode = StringHelper.clean(options.measureCode);
    this.codingUserId = options.codingUserId;
    this.codingUserName = StringHelper.clean(options.codingUserName);
    this.codingDate = options.codingDate;
    this.qaUserId = options.qaUserId;
    this.qaUserName = StringHelper.clean(options.qaUserName);
    this.qaRoleName = StringHelper.clean(options.qaRoleName);
    this.qaDate = options.qaDate;
    this.overreadLevel = StringHelper.clean(options.overreadLevel);
    this.managerUserId = options.managerUserId;
    this.managerUserName = StringHelper.clean(options.managerUserName);
    this.challengeDate = options.challengeDate;
    this.reviewDeadlineDays = options.reviewDeadlineDays;
    this.errorCount = options.errorCount;
    this.errorReviewCompletedCount = options.errorReviewCompletedCount;
    this.errorReviewToBeCompletedCount = options.errorReviewToBeCompletedCount;
    this.chaseOverreadFeedbackStatus = StringHelper.clean(options.chaseOverreadFeedbackStatus);
    this.codingReviewMode = options.codingReviewMode;
    this.recordCount = options.recordCount;
  }

  get reviewProgressText(): string {
    const completed = this.errorReviewCompletedCount ?? 0;
    const total = completed + (this.errorReviewToBeCompletedCount ?? 0);
    return total === 0 ? "" : `${completed} of ${total} reviewed`;
  }

  get reviewDeadline(): number {
    return this.reviewDeadlineDays > 0 ? this.reviewDeadlineDays : DEFAULT_REVIEW_DEADLINE_DAYS;
  }

  get daysLeft(): number | null {
    return this.challengeDate == null ? null : daysRemaining(DateHelper.parseUtc(this.challengeDate), this.reviewDeadline, new Date());
  }

  get daysLeftText(): string {
    return this.daysLeft == null ? "" : `${this.daysLeft} of ${this.reviewDeadline} days left`;
  }

  get reviewDeadlineState(): ReviewDeadlineState | null {
    return this.daysLeft == null ? null : getReviewDeadlineState(this.daysLeft);
  }

  get reviewDeadlineProgress(): number {
    return this.daysLeft == null ? 0 : Math.round((this.reviewDeadline - this.daysLeft) / this.reviewDeadline * 100);
  }
}
